import {Button, Typography, useMediaQuery} from '@material-ui/core';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {changeApplicationsFilter, changeSort, getApplications, sources, clearFilters, clearSort} from 'Agent/Policies/ducks/policyApplications';
import {theme} from 'Core/components/WithTheme';
import {ModalState, showModal} from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {constant} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import withSelectedAgency from 'Shared/components/withSelectedAgency';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';

type PolicyApplicationsProps = {
  selectedAgency: AgencyFilters;
};

const Applications = (props: PolicyApplicationsProps) => {
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const match = useRouteMatch();
  useBreadcrumbs('Policy Applications', match.url);
  const agentNumbers = useSelector(selectCurrentAgentNumbers);
  const {applications, sort, filters} = useSelector((state: ReducerState) => state.policyApplications);

  const changeFilter = withValueChangedHook(filters, changeApplicationsFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filters,
  };
  const additionalFilters = {
    isNewYork: pipe(agentNumbers, NEA.fromArray, O.map(NEA.head), O.fold(constant(false), prop('isNewYork'))),
    agentNumbers: agentNumbers.map(prop('value')),
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredPoliciesApplications',
    relativeFileLocation: `/api/policies/v1/applications/csv${addFilterParameters({
      ...filters,
      agentNumbers: agentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  useEffect(() => {
    dispatch(
      getApplications(
        selectedAgency.encryptedIdentifier,
        {...filters, ...additionalFilters},
        undefined,
        O.toUndefined(sort)
      )
    );
  }, [filters, agentNumbers, sort]);

  useEffect(() => {
    return () => {
      dispatch(clearFilters());
      dispatch(clearSort());
    }
  }, []);

  return (
    <PagedView
      pagedViewKey="applications"
      sort={O.toUndefined(sort)}
      onChangeSort={updatedSort => {
        dispatch(changeSort(updatedSort));
      }}
      data={applications}
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
        )
      }
      loadMore={page =>
        dispatch(
          getApplications(
            selectedAgency.encryptedIdentifier,
            {...filters, ...additionalFilters},
            page,
            O.toUndefined(sort)
          )
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Account Number"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
            ddprivacy="mask"
          >
            {application => (
              <Link
                to={
                  Boolean(application.eisFlag)
                  ? AccountRouteConstants.ACCOUNTS_BY_GROUP_NUMBER(application.accountNumber)
                   : AccountRouteConstants.ACCOUNTS_BY_ACCOUNT_NUMBER(application.accountNumber)
                }
              >
                <Button color="primary">
                  <Typography variant="body2">{application.accountNumber}</Typography>
                </Button>
              </Link>
            )}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            filterProperties={withStringFilterProperties(filterProperties, 'employerName')}
            default
            ddprivacy="mask"
          >
            {application => application.employerName}
          </Column>
          <Column
            {...columnProps}
            title="Owner Name"
            sortColumnKey="employeeName"
            filterProperties={withStringFilterProperties(filterProperties, 'employeeName')}
            default
            ddprivacy="mask"
          >
            {application => application.employeeName}
          </Column>
          <Column
            {...columnProps}
            title="Owner Last 4-SSN"
            sortColumnKey="employeeIdentifier"
            filterProperties={withStringFilterProperties(filterProperties, 'employeeIdentifier')}
            default
            ddprivacy="mask"
          >
            {application => application.employeeIdentifier}
          </Column>
          <Column
            {...columnProps}
            title="Source"
            filterProperties={withSelectFilterProperties(filterProperties, 'sourceTypeId', sources)}
            default
          >
            {application => (application.sourceType ? application.sourceType?.name : '')}
          </Column>
          <Column {...columnProps} title="" default isActions>
            {application =>
              application.recordInfo && application.recordInfo.length > 0 ? (
                <Button
                  color="primary"
                  style={{width: '100%'}}
                  onClick={() => {
                    dispatch(
                      showModal(
                        ModalState.EnrollmentDetails({
                          recordInfo: application.recordInfo ?? [],
                        })
                      )
                    );
                  }}
                >
                  View Details
                </Button>
              ) : null
            }
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default withSelectedAgency(Applications);
